<template>
  <div>
    <Subheader title="Edit Project" />

    <div class="app-content-column">
      <div class="card">
        <form class="form" @submit.stop.prevent="updateProject">
          <h5>Edit the details for your project</h5>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="title">
            <input
              id="title"
              v-model="project.title"
              type="text"
              placeholder="Project Name"
              required
            />
          </Field>

          <h5>PROJECT DETAILS</h5>
          <p>
            Start with a brief description of the problem and your proposed solution. Then describe
            the impact that your project will have for you and your community. Include specific
            details, # of people, timelines, etc. Every project requires a location. If you cannot
            share your location, please contact your provider for assistance!
          </p>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="headline">
            <label for="headline" class="label">Headline</label>
            <textarea
              id="headline"
              v-model="project.headline"
              placeholder="Headline that captures the project's potential impact"
              class="textarea"
              required
            />
          </Field>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="shortDescription">
            <label for="shortDescription" class="label">Brief Description</label>
            <textarea
              id="shortDescription"
              v-model="project.shortDescription"
              placeholder="Brief Description of the Problem and your Solution (limit 300 characters)"
              class="textarea"
              rows="6"
              maxlength="300"
              required
            />
          </Field>

          <Field :errors="fieldErrors" :show-field-errors="showFieldErrors" name="impact">
            <label for="impact" class="label">Desired Impact</label>
            <textarea
              id="impact"
              v-model="project.impact"
              placeholder="Description of the impact that your project will have for you and your community (limit 300 characters)"
              class="textarea"
              rows="6"
              maxlength="300"
              required
            />
          </Field>

          <div class="button-wrapper is-centered">
            <button
              type="submit"
              class="large-button"
              :disabled="loading"
              :class="{ 'get-started-btn--disabled': loading }"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Project from '@/services/projects'
import { validateInputLength } from '@/services/forms'
import { errorMessage } from '@/services/utils/apiErrors'

import Subheader from '@/components/Subheader'
import Field from '@/components/Field'

export default {
  name: 'EditProject',
  components: {
    Subheader,
    Field,
  },
  data() {
    return {
      project: Project.create(),
      fieldErrors: [],
      showFieldErrors: false,
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['getUser']),
  },
  created() {
    this.project.id = this.$route.params.projectId
    this.fetchProject()
  },
  methods: {
    async fetchProject() {
      this.loading = true
      try {
        const projectResponse = await Project.api.retrieve(this.project.id)
        this.project = projectResponse
      } catch {
        errorMessage()
      } finally {
        this.loading = false
      }
    },
    /* Update project and navigate back to Project details page */
    async updateProject() {
      this.loading = true
      this.fieldErrors = this.collectFieldErrors()

      if (this.fieldErrors.length) {
        this.showFieldErrors = true
        let element = document.getElementById('title')
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        const fieldsToUpdate = ['title', 'headline', 'shortDescription', 'impact', 'location']
        try {
          await Project.api.update(this.project, fieldsToUpdate)

          this.$Alert.alert({
            type: 'success',
            message: '<p>Project successfully updated!</p>',
            timeout: 3000,
          })
          this.$router.push({ name: 'Project', params: { projectId: this.project.id } })
        } catch {
          this.$Alert.alert({
            type: 'error',
            message:
              '<p>There was an error updating your project details. Check your connection and try again.</p>',
            timeout: 3000,
          })
        }
      }
      this.loading = false
    },
    collectFieldErrors() {
      //client-side validation
      //required: title, headline, shortDescription, impact, location
      const requiredFields = ['title', 'headline', 'shortDescription', 'impact', 'location']
      const errors = []

      requiredFields.forEach(field => {
        const error = validateInputLength(field, this.project[field])
        if (error) {
          errors.push(error)
        }
      })

      return errors
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/mixins/cards';
@import '../styles/mixins/buttons';
@import '../styles/forms';
@import '../styles/layout';

.card {
  @include card-w-borders();
}

.get-started-btn {
  @include large-button();

  &--disabled {
    background-color: $soft-gray;
    color: $base-gray;
  }
}
</style>
